import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { motion } from 'motion/react';
import { useFormContext } from 'react-hook-form';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import useNavigate from '@fuse/hooks/useNavigate';
import {
	useCreateApplicationMutation,
	useDeleteApplicationMutation,
	useUpdateApplicationMutation
} from '@/store/services/applications';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import ConfirmationDialog from "@/pages/apps/users/Dialog/ConfirmationDialog";
import { useEffect, useState } from 'react';
import { ArrowBack, Download, Edit } from '@mui/icons-material';
import useUser from '@auth/useUser';
import Link from "@fuse/core/Link";

interface AddApplicationHeaderProps {
	lineItems: any[]; // Changed from Record<number, ApplicationObject> to array
	areas: any; // Changed to match actual structure with .data property
	onSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
	applicationData?: any; // Changed to match actual structure with .data property
	deletedId?: string[];
	addressId?: number;
	selectedAddress?: { id: number; name: string | null; location_name: string | null };
}

function AddApplicationHeader({
	lineItems,
	areas,
	onSubmit,
	applicationData,
	deletedId,
	addressId,
	selectedAddress
}: AddApplicationHeaderProps) {
	const [openDialog, setOpenDialog] = useState(false);
	const [createApplication, { isLoading: isAdding }] = useCreateApplicationMutation();
	const [updateApplication, { isLoading: isUpdating }] = useUpdateApplicationMutation();
	const [removeApplication, { isLoading: isDeleting }] = useDeleteApplicationMutation();
	const { getValues } = useFormContext();
	const { data: user } = useUser();
	const [searchParams] = useSearchParams();
	const isView = searchParams.get('view') === 'true';

	const routeParams = useParams<{ applicationId: string }>();
	const { applicationId } = routeParams;

	const methods = useFormContext();
	const { formState, handleSubmit } = methods;
	const { errors } = formState;
	// console.log("errorserrorserrors", errors);
	const navigate = useNavigate();

	const handleCreateApplication = async (data: Record<string, any>) => {
		try {
			// Validate at least one line item exists
			if (!data.lineItems || data.lineItems.length === 0) {
				throw new Error('At least one item is required');
			}

			// Filter out parent items (area-only) and only process child items
			const childItems = data.lineItems.filter((item: any) => typeof item.parentIndex === 'number');

			if (childItems.length === 0) {
				throw new Error('At least one item with details is required');
			}

			const formData = new FormData();

			// Get IDs for child items only (items that have actual data)
			const childItemIds = applicationData?.details
				?.map((item) => item.id)
				?.filter((item) => !deletedId?.includes(item));

			// Process only child items (items with parentIndex)
			childItems.forEach((item: any, index: number) => {
				const prefix = `line_item[${index}]`;

				// Get the parent item to access other_area field and area_image
				const parentItem = data.lineItems[item.parentIndex];

				if (applicationId !== 'new' && childItemIds?.[index]) {
					formData.append(`${prefix}[id]`, String(childItemIds[index]));
				}

				// Handle area field first (including other_area logic)
				if (item.area === 'Other') {
					formData.append(`${prefix}[other_area]`, String(true));

					// Set the actual area value from parent's other_area field
					if (parentItem['other_area'] && parentItem['other_area'].trim() !== '') {
						formData.append(`${prefix}[area]`, String(parentItem['other_area']));
					}
				} else {
					if (item.area) {
						formData.append(`${prefix}[area]`, String(item.area));
					}

					const selectedArea = areas?.data?.find((area: any) => area.name === item.area);

					if (selectedArea?.id) {
						formData.append(`${prefix}[area_id]`, String(selectedArea?.id));
					}
				}

				// Handle name field
				if (item.name === 'Other') {
					formData.append(`${prefix}[other_line_item_name]`, String(true));

					if (item['other_line_item_name']) {
						formData.append(`${prefix}[name]`, String(item['other_line_item_name'] || null));
					}
				} else {
					if (item.name) {
						formData.append(`${prefix}[name]`, String(item.name));
					}

					const selectedLineItem = areas?.data
						?.find((ele) => ele?.name === item['area'])
						?.line_items?.find((area: any) => area.name === item.name);

					if (selectedLineItem) {
						formData.append(`${prefix}[line_item_id]`, String(selectedLineItem?.id || null));
					}
				}

				// Handle quantity field (from child item - should always be sent)
				if (item.quantity !== undefined && item.quantity !== null) {
					formData.append(`${prefix}[quantity]`, String(item.quantity));
				}

				// Handle area_image field (from parent item - parent items store the area image)
				if (parentItem?.area_image) {
					if (typeof parentItem.area_image === 'string') {
						formData.append(`${prefix}[area_image]`, parentItem.area_image);
					} else if (parentItem.area_image instanceof File) {
						formData.append(`${prefix}[area_image]`, parentItem.area_image);
					} else if (parentItem.area_image?.product_image) {
						formData.append(`${prefix}[area_image]`, parentItem.area_image.product_image.split('/').pop());
					}
				}

				// Handle other fields
				Object.entries(item).forEach(([key, value]) => {
					if (
						key !== 'images' &&
						key !== 'area' &&
						key !== 'name' &&
						key !== 'other_line_item_name' &&
						key !== 'parentIndex' &&
						key !== 'quantity' &&
						key !== 'area_image' &&
						value !== undefined &&
						value !== null
					) {
						if (value) {
							formData.append(`${prefix}[${key}]`, String(value || null));
						}
					}
				});

				item?.images?.forEach((image: any, imageIndex: number) => {
					formData.append(
						`${prefix}[images][${imageIndex + 1}]`,
						image?.product_image ? image?.product_image?.split('/')?.pop() : image
					);
				});
			});

			if (applicationId !== 'new') {
				formData.append('application_id', applicationId);
				formData.append('_method', 'PUT');
			}

			formData.append('publication_status', 'published');

			// Add address in format address if provided
			// Ensure addressId is a valid integer before sending
			console.log("addressId___",addressId)
			if (addressId !== undefined && addressId !== null) {
				const addressIdNumber = Number(addressId);

				if (!isNaN(addressIdNumber) && Number.isInteger(addressIdNumber) && addressIdNumber > 0) {
					formData.append('address', String(addressIdNumber));
				}
			} else if(applicationId !== 'new'){
				formData.append('address', String(	applicationData?.address_id));
			}

			const response =
				applicationId !== 'new' ? await updateApplication(formData) : await createApplication(formData);

			if ('error' in response) {
				throw response.error;
			}

			toast.success(`Asset ${applicationId !== 'new' ? 'updated' : 'created'} successfully`);
			navigate('/apps/applications');
		} catch (error: any) {
			const errorMessage = error?.data?.errors?.[0]?.detail || error?.message || 'Something went wrong';
			toast.error(errorMessage);
		}
	};

	const handleDelete = async () => {
		try {
			const response = await removeApplication([applicationId]);

			if ('error' in response) {
				throw response.error;
			}

			toast.success('Asset deleted successfully');
			navigate('/apps/applications');
		} catch (error: any) {
			toast.error(error?.data?.errors?.[0]?.title || 'Failed to delete asset');
		} finally {
			setOpenDialog(false);
		}
	};

	useEffect(() => {
		if (
			!['under_verification', 'rejected', ''].includes(applicationData?.application_status || '') &&
			!isView &&
			user?.role === 'Applicant'
		) {
			navigate('/apps/applications');
		}
	}, [applicationData, user, isView]);


	console.log("applicationData____",applicationData?.address_id)

	const showDownloadButtons = Number(applicationData?.is_enabled) !== 0;

	return (
		<div className="flex flex-col sm:flex-row flex-1 w-full items-center justify-between space-y-2 sm:space-y-0 py-6 sm:py-8">
			<ConfirmationDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onConfirm={handleDelete}
				title="Delete Asset"
				message="Are you sure you want to delete this asset?"
				isLoading={isDeleting}
			/>

			<div className="flex flex-col items-start space-y-2 sm:space-y-0 w-full sm:max-w-full min-w-0">
				<motion.div
					initial={{
						x: 20,
						opacity: 0
					}}
					animate={{
						x: 0,
						opacity: 1,
						transition: { delay: 0.3 }
					}}
				>
					<PageBreadcrumb className="mb-2" />
				</motion.div>

				<div className="flex items-center max-w-full space-x-3">
					<motion.div
						className="flex items-start gap-2 min-w-0"
						initial={{ x: -20 }}
						animate={{ x: 0, transition: { delay: 0.3 } }}
					>
						<div className="w-full flex items-center space-x-2">
							<Link href="/apps/applications">
								<ArrowBack />
							</Link>
						</div>
						<div className="shrink-0">
							<Typography className="text-lg sm:text-2xl truncate font-semibold">
								{applicationId !== 'new'
									? user?.role === 'Admin'
										? 'View'
										: isView
											? 'View'
											: 'Edit'
									: 'New'}{' '}
								Asset
							</Typography>

							<Typography
								variant="caption"
								className="font-medium"
							>
								{applicationId !== 'new'
									? `Created By ${applicationData?.applicant?.name}`
									: 'Asset Detail'}
							</Typography>
						</div>
					</motion.div>
				</div>
			</div>

			<motion.div
				className="flex flex-1 w-full mt-3 sm:mt-0"
				initial={{ opacity: 0, x: 20 }}
				animate={{ opacity: 1, x: 0, transition: { delay: 0.3 } }}
			>
				<div className="flex items-center justify-end w-full">
					{applicationId !== 'new' &&
					(isView || user?.role === 'Admin') &&
					applicationData?.application_status ? (
						<motion.div
							className="flex w-full justify-end"
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0, transition: { delay: 0.3 } }}
						>
							{showDownloadButtons && (
								<>
									<Button
										className="whitespace-nowrap mx-1 bg-gray-100"
										variant="contained"
										component="a"
										href={applicationData?.excel_link}
										target="_blank"
										rel="noopener noreferrer"
										startIcon={<Download />}
									>
										Download Excel
									</Button>
									<Button
										className="whitespace-nowrap mx-1 bg-[#ffca00]"
										variant="contained"
										color="secondary"
										component="a"
										href={applicationData?.pdf_link}
										target="_blank"
										rel="noopener noreferrer"
										startIcon={<Download />}
									>
										Download Pdf
									</Button>
								</>
							)}
						</motion.div>
					) : (
						!isView && (
							<Button
								className="whitespace-nowrap"
								variant="contained"
								color="secondary"
								onClick={handleSubmit(
									(data) => handleCreateApplication(data), // onValid - no isDraft, so it submits
									(errors) => {
										toast.error('Please fix the errors in the form before submitting.');

										// Find the first item with an error and open it
										if (errors.lineItems && Array.isArray(errors.lineItems)) {
											// First, try to find a child item with errors (these are the actual form items)
											const currentLineItems = getValues().lineItems || [];
											let firstErrorIndex = -1;

											// Look for child items with errors first
											for (let i = 0; i < errors.lineItems.length; i++) {
												const item = errors.lineItems[i];
												const lineItem = currentLineItems[i];

												if (
													item &&
													Object.keys(item).length > 0 &&
													lineItem &&
													typeof lineItem.parentIndex === 'number'
												) {
													firstErrorIndex = i;
													break;
												}
											}

											// If no child items with errors, look for parent items with errors
											if (firstErrorIndex === -1) {
												firstErrorIndex = errors.lineItems.findIndex(
													(item: any) => item && Object.keys(item).length > 0
												);
											}

											if (firstErrorIndex !== -1) {
												// Trigger opening the first item with error
												const event = new CustomEvent('openErrorItem', {
													detail: { index: firstErrorIndex }
												});
												window.dispatchEvent(event);
											}
										}
									}
								)}
								disabled={isAdding || isUpdating}
								startIcon={
									(isAdding || isUpdating) && (
										<CircularProgress
											size={20}
											color="inherit"
										/>
									)
								}
							>
								Save
							</Button>
						)
					)}
					{['under_verification', 'rejected', ''].includes(applicationData?.application_status || '') &&
						user?.role === 'Applicant' &&
						isView && (
							<>
								{showDownloadButtons && (
									<>
										<Button
											className="whitespace-nowrap mx-1 bg-gray-100"
											variant="contained"
											component="a"
											href={applicationData?.excel_link}
											target="_blank"
											rel="noopener noreferrer"
											startIcon={<Download />}
										>
											Download Excel
										</Button>
										<Button
											className="whitespace-nowrap mx-1 bg-[#ffca00]"
											variant="contained"
											color="secondary"
											component="a"
											href={applicationData?.pdf_link}
											target="_blank"
											rel="noopener noreferrer"
											startIcon={<Download />}
										>
											Download Pdf
										</Button>
									</>
								)}
								{showDownloadButtons && (
									<Button
										className="whitespace-nowrap bg-[#ffca00] ml-1"
										variant="contained"
										color="secondary"
										onClick={() => navigate(`/apps/applications/${applicationId}`)}
										startIcon={<Edit />}
									>
										Edit
									</Button>
								)}
							</>
						)}
				</div>
			</motion.div>
		</div>
	);
}

export default AddApplicationHeader;
